import { Operation } from "types";

/**
 * Retrieves tasks that belong to a specified group.
 *
 * @param groupName - The name of the group to filter tasks by.
 * @param tasks - An array of tasks to filter.
 * @returns An array of tasks that belong to the specified group.
 */
export const getTasksByGroup = (
  groupId: number,
  tasks: Operation[]
): Operation[] => {
  try {
    const tasksByGroup = tasks.filter((task) => task.groupId === groupId);
    return tasksByGroup;
  } catch (error) {
    return [];
  }
};

/**
 * Retrieves a list of distinct statuses from an array of operations.
 *
 * @param tasks - An array of operations from which to extract statuses.
 * @returns An array of unique status strings. If an error occurs, an empty array is returned.
 */
export const getDistinctStatuses = (tasks: Operation[]): string[] => {
  try {
    const statuses = tasks.map((task) => task.status);
    return Array.from(new Set(statuses));
  } catch (error) {
    return [];
  }
};

/**
 * Filters a list of operations based on the provided filter values.
 *
 * @param {Operation[]} operations - The list of operations to filter.
 * @param {any} values - The filter values.
 * @param {string} [values.searchText=""] - The text to search for in task titles.
 * @param {boolean} [values.isAssignedToMe=false] - Whether to filter operations assigned to the current user.
 * @param {string} [values.currentUserEmail=""] - The email of the current user.
 * @returns {Operation[]} The filtered list of operations.
 */
export const filterOperations = (
  tasks: Operation[],
  values: any
): Operation[] => {
  try {
    const {
      searchText = "",
      isAssignedToMe = false,
      currentUserEmail = "",
    } = values;
    return tasks.filter((task: Operation) => {
      const matchesSearchText =
        task.title.toLowerCase().includes(searchText.toLowerCase()) ||
        task.groupName.toLowerCase().includes(searchText.toLowerCase());
      const matchesAssignee = task.primaryAssignee.email === currentUserEmail;
      const matches = isAssignedToMe
        ? matchesSearchText && matchesAssignee
        : matchesSearchText;
      return matches;
    });
  } catch (error) {
    return [];
  }
};

/**
 * Generates an array of options with ordinal suffixes (e.g., "1st", "2nd", "3rd").
 *
 * @param max - The maximum number to generate options for (inclusive).
 * @param tail - An optional string to append to each label (e.g., "day", "item").
 * @returns An array of objects, each containing a `label` (string) and `value` (number).
 *
 * @example
 * generateNthOptions(3);
 * // Output: [
 * //   { label: "1st", value: 1 },
 * //   { label: "2nd", value: 2 },
 * //   { label: "3rd", value: 3 }
 * // ]
 *
 * @example
 * generateNthOptions(3, "day");
 * // Output: [
 * //   { label: "1st day", value: 1 },
 * //   { label: "2nd day", value: 2 },
 * //   { label: "3rd day", value: 3 }
 * // ]
 */
export const generateNthOptions = (max: number, tail?: string) => {
  return Array.from({ length: max }, (_, i) => {
    const value = i + 1;
    let suffix = "th";
    if (value === 1 || value === 21 || value === 31) suffix = "st";
    else if (value === 2 || value === 22) suffix = "nd";
    else if (value === 3 || value === 23) suffix = "rd";
    return {
      label: `${value}${suffix}${tail ? ` ${tail}` : ""}`,
      value: value,
    };
  });
};

/**
 * Generates an array of day options for a given month of the year.
 *
 * @param month - The month (1 for January, 2 for February, etc.).
 * @param year - The year to account for leap years in February.
 * @returns An array of objects, each containing a `label` (string) and `value` (number).
 *
 * @example
 * generateDaysForMonth(2, 2024);
 * // Output: [
 * //   { label: "1st", value: 1 },
 * //   { label: "2nd", value: 2 },
 * //   ...,
 * //   { label: "29th", value: 29 }
 * // ]
 */
export const generateDaysForMonth = (month: number, year: number) => {
  const daysInMonth = new Date(year, month, 0).getDate(); // Get the number of days in the month
  return generateNthOptions(daysInMonth);
};

/**
 * Checks if a group with the specified name exists in the provided groups array.
 *
 * @param groups - The groups object containing a data array.
 * @param values - The values object containing the selected group name.
 * @returns {boolean} True if the group does not exist, false otherwise.
 */
export const isGroupNameUnique = (groups: any, userInput: any): boolean => {
  try {
    return (
      Array.isArray(groups?.data) &&
      !groups?.data.some((gp: any) => gp.groupName === userInput)
    );
  } catch (error) {
    return false;
  }
};
